import { useRouter } from 'next/router';
import { Tooltip } from '@mui/material';
import { SysAsset } from '../../types/contentstack';
import {
  FunctionComponent,
  CSSProperties,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';
import styled, { keyframes } from 'styled-components';
import { ResponsiveContainer } from '../responsive';

export const LogoCarousel: FunctionComponent<{
  direction?: 'left' | 'right';
  style?: CSSProperties;
  title?: string;
  height?: number;
  data?: { href?: string; title?: string; file: SysAsset }[];
  tintColor?: string;
  speedSeconds?: number;
}> = ({
  direction = 'left',
  style,
  title,
  height = 80,
  data = [],
  tintColor = '#5d6e8e',
  speedSeconds,
}) => {
  const router = useRouter();
  // Duplicate list to guarantee scrollable distance even with few logos
  const logos = useMemo(() => (data.length ? [...data, ...data] : []), [data]);

  const trackRef = useRef<HTMLDivElement>(null);
  const [trackWidth, setTrackWidth] = useState(0);

  useEffect(() => {
    if (!trackRef.current) return;
    const measure = () => {
      if (trackRef.current) setTrackWidth(trackRef.current.scrollWidth || 0);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(trackRef.current);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [data.length]);

  const animation = useMemo(() => {
    const half = trackWidth / 2; // width of one logical sequence of logos
    if (!half) return keyframes``;

    if (direction === 'left') {
      return keyframes`
        0% { transform: translateX(0); }
        100% { transform: translateX(-${half}px); }
      `;
    }

    return keyframes`
      0% { transform: translateX(-${half}px); }
      100% { transform: translateX(0); }
    `;
  }, [direction, trackWidth]);

  // Base speed relative to number of unique logos if not provided
  const half = trackWidth / 2;
  const baseDuration =
    (speedSeconds || (half ? Math.max(18, half / 10) : 0)) * 0.5;

  if (!data.length) return null;

  return (
    <Wrapper
      style={style}
      aria-label={title}
      role={title ? 'region' : undefined}
    >
      <Viewport $height={height}>
        <Track
          ref={trackRef}
          $animation={animation}
          $duration={baseDuration}
          $direction={direction}
          style={{ visibility: trackWidth ? 'visible' : 'hidden' }}
        >
          {logos.map(({ href, title: logoTitle, file }, idx) => {
            const filename = file?.url
              ?.split('/')
              ?.pop()
              ?.split('.')
              ?.slice(0, -1)
              ?.join('.');

            const key = (file?.url || 'logo') + idx;
            const ratio =
              file?.dimension?.width && file?.dimension?.height
                ? file.dimension.width / file.dimension.height
                : 2.5;
            return (
              <Tooltip
                key={key}
                title={logoTitle || ''}
                arrow
                disableHoverListener={!logoTitle}
                placement="bottom"
              >
                <Tile
                  $height={height}
                  onClick={() => href && router.push(href)}
                  tabIndex={href ? 0 : -1}
                  role={href ? 'link' : 'img'}
                  aria-label={logoTitle || filename || 'logo'}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && href) router.push(href);
                  }}
                >
                  <MaskedLogo
                    style={{ ['--ratio' as any]: ratio.toString() }}
                    $src={file?.url || ''}
                    $color={tintColor}
                  />
                </Tile>
              </Tooltip>
            );
          })}
        </Track>
        <EdgeFade className="left" />
        <EdgeFade className="right" />
      </Viewport>
    </Wrapper>
  );
};

const Wrapper = styled(ResponsiveContainer)`
  width: 100%;
  position: relative;
  user-select: none;
  pointer-events: none;
`;

const Viewport = styled.div<{ $height: number }>`
  width: 100%;
  overflow: hidden;
  position: relative;
  height: ${(p) => p.$height}px;
`;

const Track = styled.div<{
  $animation: ReturnType<typeof keyframes>;
  $duration: number;
  $direction: 'left' | 'right';
}>`
  display: flex;
  align-items: stretch;
  animation: ${(p) => p.$animation} ${(p) => p.$duration}s linear infinite;
  &:hover {
    animation-play-state: paused;
  }
`;

const Tile = styled.div<{ $height: number }>`
  --tile-bg: #262f40;
  --tile-bg-hover: #303b4d;
  --tile-radius: 8px;

  display: flex;
  align-items: center;
  justify-content: center;
  height: ${(p) => p.$height}px;
  padding: 0 42px;
  margin-right: 25px;
  background: var(--tile-bg);
  border-radius: var(--tile-radius);
  cursor: pointer;
  position: relative;
  flex: 0 0 auto;
  transition:
    background 0.25s ease,
    opacity 0.25s ease;
  outline: none;

  &:last-child {
    margin-right: 0;
  }
  &:focus-visible {
    box-shadow: 0 0 0 2px #fff3 inset;
  }
  &:hover {
    background: var(--tile-bg-hover);
  }

  &[aria-label='Switch_Automation'] {
    > div {
      height: 68%;
    }
  }
`;

const MaskedLogo = styled.div<{ $src: string; $color: string }>`
  width: auto;
  height: 40%;
  aspect-ratio: var(--ratio, 3);
  background: ${(p) => p.$color};
  -webkit-mask-image: url(${(p) => p.$src});
  mask-image: url(${(p) => p.$src});
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
  -webkit-mask-size: contain;
  mask-size: contain;
  transition: opacity 0.25s ease;

  ${Tile}:hover & {
    opacity: 1;
  }
`;

const EdgeFade = styled.div`
  pointer-events: none;
  position: absolute;
  top: 0;
  bottom: 0;
  width: 240px;
  z-index: 2;
  &.left {
    left: 0;
    background: linear-gradient(90deg, #181e2b 0%, #181e2b00 90%);
  }
  &.right {
    right: 0;
    background: linear-gradient(270deg, #181e2b 0%, #181e2b00 90%);
  }
`;
